import {
  Autocomplete,
  AutocompleteRenderInputParams,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { galleryRootZIndex } from 'components/common/gallery/styles';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { WithCreateCompany } from 'components/dataCreationForms/company/withCreateCompany';
import { WithCreateOrganization } from 'components/dataCreationForms/organization/withCreateOrganization';
import { WithCreateSite } from 'components/dataCreationForms/site/withCreateSite';
import { WithCreateLevel } from 'components/dataCreationForms/level/withCreateLevel';
import { WithCreateUser } from 'components/dataCreationForms/user/withCreateUser';
import React, {
  PropsWithChildren,
  Ref,
  SyntheticEvent,
  forwardRef,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { LabelledEntity } from 'shared/types/commonView';
import { DependanceBehavior } from 'shared/types/form';
import {
  formHasSiteField,
  getSiteIdFromValues,
  getAutocompleteRenderInput,
  getFreeSoloProps,
  getHelperTextFromError,
  getLocalizedLabel,
  getSiteIdFromForm,
  isOptionEqualToValue,
  unifiedGetOptionLabel,
} from '../common';
import { FreeSoloSingleAutocomplete } from './freeSoloSingleAutocomplete';
import { SelectedValue } from './selectedValue';
import { SingleOption } from './singleOption';
import { SingleChoiceProps, SingleChoiceValue } from './types';
import { NotificationTranslationKeys } from 'intl/keys';
import { FieldTranslationKeys } from 'intl/keys';

function WithCreateSiteForForm({
  children,
  onSelect,
}: PropsWithChildren<{
  onSelect: (entity: LabelledEntity) => void;
}>): React.ReactElement {
  const onSuccessCallback = useCallback(
    (_id: string, label: string) => onSelect({ _id, label }),
    [onSelect]
  );

  return (
    <WithCreateSite onSuccessCallback={onSuccessCallback}>
      {children as React.ReactElement}
    </WithCreateSite>
  );
}

function WithCreateLevelForForm({
  children,
  onSelect,
}: PropsWithChildren<{
  onSelect: (entity: LabelledEntity) => void;
}>): React.ReactElement {
  const onSuccessCallback = useCallback(
    (_id: string, label: string) => onSelect({ _id, label }),
    [onSelect]
  );

  return (
    <WithCreateLevel onSuccessCallback={onSuccessCallback}>
      {children as React.ReactElement}
    </WithCreateLevel>
  );
}

function WithCreateUserForForm({
  children,
  onSelect,
}: PropsWithChildren<{
  onSelect: (entity: LabelledEntity) => void;
}>): React.ReactElement {
  const onSuccessCallback = useCallback(
    (_id: string, label: string) => onSelect({ _id, label }),
    [onSelect]
  );

  return (
    <WithCreateUser onSuccessCallback={onSuccessCallback}>
      {children as React.ReactElement}
    </WithCreateUser>
  );
}

function WithCreateCompanyForForm({
  children,
  onSelect,
}: PropsWithChildren<{
  onSelect: (entity: LabelledEntity) => void;
}>): React.ReactElement {
  const onSuccessCallback = useCallback(
    (_id: string, label: string) => onSelect({ _id, label }),
    [onSelect]
  );

  return (
    <WithCreateCompany onSuccessCallback={onSuccessCallback}>
      {children as React.ReactElement}
    </WithCreateCompany>
  );
}

function WithCreateOrganizationForForm({
  children,
  onSelect,
}: PropsWithChildren<{
  onSelect: (entity: LabelledEntity) => void;
}>): React.ReactElement {
  const onSuccessCallback = useCallback(
    (_id: string, label: string) => onSelect({ _id, label }),
    [onSelect]
  );

  return (
    <WithCreateOrganization onSuccessCallback={onSuccessCallback}>
      {children as React.ReactElement}
    </WithCreateOrganization>
  );
}

const SingleChoice = (
  {
    available,
    value,
    handleChange,
    disabled,
    id,
    required,
    labelId,
    localisedLabel,
    error,
    disableClearable,
    hidden,
    field,
    entityName,
    reserveSpaceForHelperText,
    dense,
    formValuesGetter,
    issueParams,
    omitOptionalEmptyMenuItem,
  }: SingleChoiceProps,
  ref: Ref<HTMLDivElement>
): React.ReactElement => {
  const intl = useIntl();
  const hideBehaviorSx =
    field?.dependsOn?.behavior === DependanceBehavior.hidden
      ? {
          animation: 'fadeInFromNone 0.33s ease-out',
          height: 'auto',
        }
      : {};
  const hiddenSx = hidden
    ? {
        opacity: 0,
        height: 0,
        display: 'none',
      }
    : {};
  const label = useMemo(
    () => getLocalizedLabel(intl, localisedLabel, labelId),
    [intl, localisedLabel, labelId]
  );
  const autocompleteHandleChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      value: SingleChoiceValue | null
    ) => {
      handleChange(event, value);
    },
    [handleChange]
  );

  const selectHandleChange = useCallback(
    (event: any, _: React.ReactNode) => {
      handleChange(
        event,
        available.find((item) => item._id === event.target.value)
      );
    },
    [handleChange, available]
  );
  let helperText =
    getHelperTextFromError(error, intl) ||
    // TODO MUI5 pomysł jest dobry, ale wyświetla się to od razu przy podlokalizacji na dialogu tworzenia issue - trzeba to przemyśleć
    //  (available.length === 0 &&
    //   formatTranslation(intl, CommonTranslationKeys.autocompleteNoOptions)) ||
    undefined;

  let siteId = '';
  const formValues = formValuesGetter?.().values;
  const companyNeedsSite =
    entityName === EditableStandardEntityName.company &&
    formValues != null &&
    formHasSiteField(formValues);
  if (
    entityName === EditableStandardEntityName.level ||
    companyNeedsSite
  ) {
    siteId =
      formValues != null
        ? getSiteIdFromValues(formValues)
        : getSiteIdFromForm(formValuesGetter);
    if (!siteId) {
      // TODO: workaround until we disable fields properly on form level based on dependsOn values
      disabled = true;
      helperText = formatTranslation(intl, NotificationTranslationKeys.issueFormDependedNotFilled , { fieldLabel: formatTranslation(intl, FieldTranslationKeys.site ) }
      );
    }
  }
  const sx = { ...hideBehaviorSx, ...hiddenSx };

  const freeSoloProps = entityName && getFreeSoloProps(entityName);

  const onCreatedEntitySelect = useCallback(
    (entity: LabelledEntity) => {
      handleChange(null, entity);
    },
    [handleChange]
  );

  const autoCompleteInputRenderer = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return getAutocompleteRenderInput({
        required,
        error,
        label,
        disabled: !!disabled,
        id: `${id}-textField`,
        placeholder: '',
        dense,
      })(params);
    },
    [required, error, label, disabled, id, dense]
  );

  const renderOption = useCallback(
    (props, item) => {
      const { key, ...restProps } = props;
      return (
        <SingleOption
          key={item._id}
          item={item}
          value={item._id}
          entityName={entityName}
          issueParams={issueParams}
          {...restProps}
        />
      );
    },
    [issueParams, entityName, handleChange]
  );

  const autoCompleteProps = useMemo(
    () => ({
      options: available,
      disabled: Boolean(disabled),
      value: value || undefined,
      isOptionEqualToValue: isOptionEqualToValue,
      id: `${id}-singleAutocomplete`,
      onChange: autocompleteHandleChange,
      ref,
      renderInput: autoCompleteInputRenderer,
      renderOption: renderOption,
      getOptionLabel: unifiedGetOptionLabel,
    }),
    [
      available,
      disabled,
      value,
      id,
      autocompleteHandleChange,
      ref,
      autoCompleteInputRenderer,
    ]
  );
  // popper stays open after click on map on issue preview
  // field?.name is provided only on issue form so its nasty workaround
  if (field?.name) {
    //@ts-ignore
    autoCompleteProps.slotProps = {
      popper: { sx: { zIndex: galleryRootZIndex - 1 } },
    };
  }

  const availableForSelect = useMemo(() => {
    const listForSelect: (LabelledEntity | '')[] = [...available];
    if (listForSelect.length === 0 && value) {
      listForSelect.push(value);
    }
    if (!required && !omitOptionalEmptyMenuItem) {
      listForSelect.unshift('');
    }
    return listForSelect;
  }, [available, required, value, omitOptionalEmptyMenuItem]);
  // on why we provide label twice https://stackoverflow.com/a/67068903
  const displayedLabel = `${label}${required ? ' *' : ''}`;

  const renderSelectValue = useCallback(
    (id: string) => {
      const option = available.find((option) => option._id === id);
      return (
        <SelectedValue
          option={option}
          entityName={entityName}
          issueParams={issueParams}
        />
      );
    },
    [available, entityName, issueParams]
  );

  if (freeSoloProps) {
    const freeSoloFormControl = (
      <FormControl
        data-qa={'form-control-single-freeSolo'}
        fullWidth
        ref={ref}
        sx={sx}
      >
        <FreeSoloSingleAutocomplete
          autoCompleteProps={autoCompleteProps}
          useProvider={freeSoloProps.useProvider}
          siteId={siteId}
        />
        {freeSoloProps.DialogComponent}
        <FormHelperText error={!!error}>
          {helperText || (reserveSpaceForHelperText && ' ')}
        </FormHelperText>
      </FormControl>
    );

    if (entityName === EditableStandardEntityName.location) {
      return (
        <WithCreateSiteForForm onSelect={onCreatedEntitySelect}>
          {freeSoloFormControl}
        </WithCreateSiteForForm>
      );
    }

    if (entityName === EditableStandardEntityName.level) {
      return (
        <WithCreateLevelForForm onSelect={onCreatedEntitySelect}>
          {freeSoloFormControl}
        </WithCreateLevelForForm>
      );
    }

    if (entityName === EditableStandardEntityName.user) {
      return (
        <WithCreateUserForForm onSelect={onCreatedEntitySelect}>
          {freeSoloFormControl}
        </WithCreateUserForForm>
      );
    }

    if (entityName === EditableStandardEntityName.company) {
      return (
        <WithCreateCompanyForForm onSelect={onCreatedEntitySelect}>
          {freeSoloFormControl}
        </WithCreateCompanyForForm>
      );
    }

    if (entityName === EditableStandardEntityName.organization) {
      return (
        <WithCreateOrganizationForForm onSelect={onCreatedEntitySelect}>
          {freeSoloFormControl}
        </WithCreateOrganizationForForm>
      );
    }

    return (
      <freeSoloProps.Provider>{freeSoloFormControl}</freeSoloProps.Provider>
    );
  }

  if (available.length > 6) {
    // TODO add empty option like in select below
    return (
      <FormControl
        data-qa={'form-control-singleAutocomplete'}
        fullWidth
        ref={ref}
        sx={sx}
      >
        <Autocomplete {...autoCompleteProps} />
        <FormHelperText error={!!error}>
          {helperText || (reserveSpaceForHelperText && ' ')}
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <Tooltip
      title={
        typeof disabled === 'object'
          ? formatTranslation(intl, unsafeTranslationKey(disabled.reason) )
          : undefined
      }
    >
      <FormControl
        data-qa={'form-control-single-select'}
        fullWidth
        ref={ref}
        sx={sx}
        margin={dense ? 'dense' : 'none'}
      >
        <InputLabel id={`${id}-inputLabel`}>{displayedLabel}</InputLabel>
        <Select
          sx={{
            '&:hover .MuiSvgIcon-root': {
              visibility: 'visible',
            },
          }}
          value={value?._id || ''}
          label={displayedLabel}
          onChange={selectHandleChange}
          id={`${id}-select`}
          disabled={Boolean(disabled)}
          required={!!required}
          renderValue={renderSelectValue}
        >
          {availableForSelect.map((option, index) =>
            option ? (
              <SingleOption
                key={index}
                value={option._id}
                item={option}
                entityName={entityName}
                issueParams={issueParams}
              />
            ) : (
              <MenuItem
                sx={{ height: '28px' }}
                key={'empty'}
                value={undefined}
              >
                {' '}
              </MenuItem>
            )
          )}
        </Select>
        <FormHelperText error={!!error}>
          {helperText || (reserveSpaceForHelperText && ' ')}
        </FormHelperText>
      </FormControl>
    </Tooltip>
  );
};

const ForwardedSingleChoice = forwardRef<
  HTMLDivElement,
  SingleChoiceProps
>(SingleChoice) as React.ForwardRefExoticComponent<SingleChoiceProps>;

export const MemoSingleChoice = memo(ForwardedSingleChoice);
